import { CodeValueType } from '@/types/types';
import { MemberCoach } from '@/features/memberCoach/types';
import { TicketTransferOption, TicketTransferSessionNumberPolicy, TransferTicketBody } from './types';

/**
 * 양도 수강권의 담당 강사 선택.
 *
 * - `KEEP`   : 원본 수강권의 강사 그대로(기본값, 기존 동작).
 * - `CHANGE` : 관리자가 고른 다른 강사. `coachId` 가 있어야 완료된 선택이다.
 *
 * 그룹 수강권(MEMBER_TICKET)은 강사 개념이 없어 어떤 선택이든 무시된다 — 요청 본문에도
 * 절대 실리지 않는다(서버가 400 으로 거부한다).
 */
export type TransferCoachSelection = {
    mode: 'KEEP' | 'CHANGE';
    coachId?: string;
};

export const KEEP_COACH_SELECTION: TransferCoachSelection = { mode: 'KEEP' };

export const isCoachSelectableOption = (
    option: Pick<TicketTransferOption, 'sourceType'> | null | undefined,
): boolean => option?.sourceType === 'MEMBERSHIP';

/**
 * 실제로 서버에 보낼 강사 변경 값. "다른 강사" 를 골랐지만 원본과 같은 강사라면 변경이
 * 아니므로 null 이다. 그룹 수강권도 항상 null.
 *
 * `availableCoachIds` 를 주면 **지금 화면에 있는 강사만** 인정한다. 고른 뒤 강사 목록이
 * 다시 불려 와 그 강사가 빠졌으면(퇴사 등) 화면에는 없는 ID 가 조용히 전송되지 않게
 * null 로 떨어뜨린다 — 그러면 완료 판정이 "미완료" 가 되어 제출이 막힌다.
 */
export const resolveChangedCoachId = (
    option: Pick<TicketTransferOption, 'sourceType' | 'coachId'> | null | undefined,
    selection: TransferCoachSelection,
    availableCoachIds?: Iterable<string> | null,
): string | null => {
    if (!option || !isCoachSelectableOption(option)) return null;
    if (selection.mode !== 'CHANGE') return null;
    const coachId = (selection.coachId || '').trim();
    if (!coachId || coachId === (option.coachId || '')) return null;
    if (availableCoachIds && !new Set(availableCoachIds).has(coachId)) return null;
    return coachId;
};

/**
 * 제출을 막아야 하는 미완료 상태인가. "다른 강사" 를 골라 놓고 강사를 아직 안 고른 경우,
 * 또는 원본과 같은 강사를 고른 경우다(그건 '유지' 로 돌아가야 한다).
 */
export const isCoachSelectionIncomplete = (
    option: Pick<TicketTransferOption, 'sourceType' | 'coachId'> | null | undefined,
    selection: TransferCoachSelection,
    availableCoachIds?: Iterable<string> | null,
): boolean => {
    if (!option || !isCoachSelectableOption(option)) return false;
    if (selection.mode !== 'CHANGE') return false;
    return resolveChangedCoachId(option, selection, availableCoachIds) === null;
};

/**
 * 강사 선택은 "이 수강권을 이 회원에게" 라는 맥락에 묶인다. 수강권이나 받는 회원이
 * 바뀌면 이전 맥락의 선택은 무효다 — effect 로 뒤늦게 지우지 않고, 키가 다르면
 * 그 자리에서 '유지' 로 읽는다(한 렌더라도 stale 선택이 요청에 실리지 않게).
 */
export const makeCoachSelectionKey = (sourceId: string, recipientId: string): string =>
    `${sourceId || ''}|${recipientId || ''}`;

export const readCoachSelectionForKey = (
    stored: { key: string; selection: TransferCoachSelection } | null | undefined,
    key: string,
): TransferCoachSelection => (stored && stored.key === key ? stored.selection : KEEP_COACH_SELECTION);

/**
 * 요청 본문. `toCoachId` 는 **원본 강사와 다를 때만** 넣는다.
 * 구버전 서버(Joi 가 모르는 키를 400 으로 거부)에서 강사를 유지하는 양도가 그대로 되게
 * 하기 위한 호환 규칙이다. 다른 강사를 고른 요청만 구서버에서 명확히 실패한다.
 */
export const buildTransferTicketBody = ({
    option,
    toMemberId,
    transferCount,
    policy,
    memo,
    selection,
    availableCoachIds,
}: {
    option: Pick<TicketTransferOption, 'sourceType' | 'sourceId' | 'coachId'>;
    toMemberId: string;
    transferCount: number;
    policy: TicketTransferSessionNumberPolicy;
    memo?: string;
    selection: TransferCoachSelection;
    availableCoachIds?: Iterable<string> | null;
}): TransferTicketBody => {
    const body: TransferTicketBody = {
        sourceType: option.sourceType,
        sourceId: option.sourceId,
        toMemberId,
        transferCount,
        targetSessionNumberPolicy: policy,
        memo,
    };
    const changedCoachId = resolveChangedCoachId(option, selection, availableCoachIds);
    if (changedCoachId) {
        body.toCoachId = changedCoachId;
    }
    return body;
};

export type RecipientCoachSuggestion = {
    coachId: string;
    name: string;
    isPrimary: boolean;
};

/**
 * "받는 회원의 담당 강사" 바로가기 칩. 원본 강사는 제외한다(그건 '유지' 다).
 * 센터 강사 목록(`coachOptions`)에 없는 강사(퇴사·타센터)는 서버가 거부하므로 보여 주지 않는다.
 * 이름은 센터 목록의 표시명(별칭 반영)을 우선한다.
 */
export const resolveRecipientCoachSuggestions = ({
    recipientCoaches,
    coachOptions,
    sourceCoachId,
}: {
    recipientCoaches: Pick<MemberCoach, 'coachId' | 'coachName' | 'isPrimary' | 'relationStatus'>[] | null | undefined;
    coachOptions: CodeValueType[] | null | undefined;
    sourceCoachId?: string;
}): RecipientCoachSuggestion[] => {
    const optionNameById = new Map((coachOptions ?? []).map((option) => [option.code, option.value]));
    const seen = new Set<string>();
    const suggestions: RecipientCoachSuggestion[] = [];
    for (const relation of recipientCoaches ?? []) {
        const coachId = (relation.coachId || '').trim();
        if (!coachId || seen.has(coachId)) continue;
        if (coachId === (sourceCoachId || '')) continue;
        if (!optionNameById.has(coachId)) continue;
        seen.add(coachId);
        suggestions.push({
            coachId,
            name: optionNameById.get(coachId) || relation.coachName || '',
            isPrimary: relation.isPrimary === true,
        });
    }
    // 주담당을 앞으로.
    return suggestions.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
};
